import { useState } from 'react';
import {
  FaBuilding, FaDollarSign, FaChartBar, FaExchangeAlt,
  FaBell, FaArrowRight, FaCheckCircle, FaShieldAlt, FaBolt,
  FaClipboardList, FaLightbulb, FaHome, FaEnvelope
} from 'react-icons/fa';
import logo from '../assets/GuideWell_Blue.png';
import '../styles/LandingPage.css';

const BASE_URL = 'https://api.dtecx.net:8000';

const features = [
  {
    icon: <FaBuilding />,
    title: 'Property Management',
    desc: 'Track every property and unit in one place. Monitor occupancy, add new properties, and manage tenant details with ease.',
    color: '#2563eb',
    bg: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
  },
  {
    icon: <FaDollarSign />,
    title: 'Financial Tracking',
    desc: 'Stay on top of income and expenses. See your net profit at a glance and compare month-over-month performance.',
    color: '#16a34a',
    bg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
  },
  {
    icon: <FaChartBar />,
    title: 'Cash Flow Reports',
    desc: 'Visual cash flow charts and detailed reports that give you a clear picture of your portfolio\'s financial health.',
    color: '#7c3aed',
    bg: 'linear-gradient(135deg, #faf5ff 0%, #ede9fe 100%)',
  },
  {
    icon: <FaExchangeAlt />,
    title: 'Compare Properties',
    desc: 'Side-by-side comparisons across your portfolio. Identify your best performers and spot opportunities to improve.',
    color: '#0891b2',
    bg: 'linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)',
  },
  {
    icon: <FaBell />,
    title: 'Smart Reminders',
    desc: 'Never miss a deadline. Get automatic alerts for upcoming rent, expiring leases, and insurance renewals.',
    color: '#d97706',
    bg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
  },
  {
    icon: <FaShieldAlt />,
    title: 'Secure & Reliable',
    desc: 'Enterprise-grade security powered by Firebase. Your property data is encrypted and always protected.',
    color: '#dc2626',
    bg: 'linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)',
  },
];

/* ── Inline mock UI components ── */
function UiSidebar({ active }) {
  const top = [
    { label: 'Dashboard',  icon: <FaHome /> },
    { label: 'Properties', icon: <FaBuilding /> },
    { label: 'Financials', icon: <FaDollarSign /> },
    { label: 'Reports',    icon: <FaChartBar /> },
    { label: 'Compare',    icon: <FaExchangeAlt /> },
  ];
  return (
    <div className="ui-sidebar">
      <div className="ui-sidebar-brand">
        <span className="ui-brand-icon"><FaBuilding /></span>
        <span className="ui-brand-text">GUIDEWELL</span>
      </div>
      <div className="ui-sb-nav">
        {top.map(item => (
          <div key={item.label} className={`ui-sb-item ${item.label === active ? 'ui-sb-active' : ''}`}>
            <span className="ui-sb-icon">{item.icon}</span>
            <span className="ui-sb-label">{item.label}</span>
          </div>
        ))}
      </div>
      <div className="ui-sb-bottom">
        <div className="ui-sb-item"><span className="ui-sb-icon"><FaShieldAlt /></span><span className="ui-sb-label">Settings</span></div>
        <div className="ui-sb-item"><span className="ui-sb-icon"><FaArrowRight /></span><span className="ui-sb-label">Logout</span></div>
      </div>
    </div>
  );
}

function MockDashboard() {
  const chips = [
    {v:'5', l:'Total Properties', bg:'#dcfce7', c:'#16a34a'},
    {v:'8', l:'Total Doors',      bg:'#fff',    c:'#2563eb', border:'#bfdbfe'},
    {v:'3', l:'Vacant',           bg:'#fef9c3', c:'#ca8a04'},
    {v:'5', l:'Occupied',         bg:'#d1fae5', c:'#059669'},
  ];
  return (
    <div className="ui-app">
      <UiSidebar active="Dashboard" />
      <div className="ui-main">
        <div className="ui-topbar">
          <div>
            <p className="ui-page-title">Dashboard</p>
            <p className="ui-page-sub">Welcome back, here's what's happening</p>
          </div>
          <div className="ui-topbar-right">
            <div className="ui-search-bar"><span>🔍</span> Search</div>
            <div className="ui-avatar">SW</div>
          </div>
        </div>

        <div className="ui-section-label">Quick Stats</div>
        <div className="ui-chips-row">
          {chips.map(s => (
            <div className="ui-chip" key={s.l} style={{background:s.bg, border:`1px solid ${s.border||s.c+'44'}`}}>
              <small style={{color:'#6b7280'}}>{s.l}</small>
              <strong style={{color:s.c, fontSize:'1.4rem'}}>{s.v}</strong>
            </div>
          ))}
        </div>

        <div className="ui-fin-row">
          {[
            {l:'Total Rent',     v:'$18,450', tag:'+8% from last month',  bg:'#16a34a'},
            {l:'Total Expenses', v:'$5,230',  tag:'-3% from last month',  bg:'#dc2626'},
            {l:'Net Profit',     v:'$13,220', tag:'+12% from last month', bg:'#2563eb'},
          ].map(c => (
            <div className="ui-fin-card" key={c.l} style={{background:c.bg}}>
              <small>{c.l}</small>
              <strong>{c.v}</strong>
              <div className="ui-fin-tag">{c.tag}</div>
            </div>
          ))}
        </div>

        <div className="ui-bottom-row">
          <div className="ui-chart-card">
            <p className="ui-card-label">Cash Flow Overview</p>
            <p className="ui-card-sub">Net monthly cash flow (YTD)</p>
            <div className="ui-bars">
              {[30,55,40,70,50,65,85,60,75,45,90,68].map((h,i) => (
                <div key={i} className="ui-bar" style={{height:`${h}%`, background: h > 50 ? '#16a34a' : '#dc2626'}} />
              ))}
            </div>
            <div className="ui-chart-legend">
              <span><span className="ui-leg-dot" style={{background:'#16a34a'}} /> Positive</span>
              <span><span className="ui-leg-dot" style={{background:'#dc2626'}} /> Negative</span>
            </div>
          </div>
          <div className="ui-occ-card">
            <p className="ui-card-label">Occupancy Rate</p>
            <div className="ui-occ-ring"><span>63%</span></div>
            <div className="ui-occ-footer">
              <span>Occupied Units</span><strong>5 / 8</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MockProperties() {
  const chips = [
    {v:'5', l:'TOTAL PROPERTIES', bg:'#dcfce7', c:'#16a34a'},
    {v:'8', l:'TOTAL DOORS',      bg:'#fff',    c:'#2563eb', border:'#bfdbfe'},
    {v:'5', l:'OCCUPIED',         bg:'#dcfce7', c:'#16a34a'},
    {v:'3', l:'VACANT',           bg:'#ede9fe', c:'#7c3aed'},
  ];
  const props = [
    {no:'No. 1', name:'Maple Grove',    type:'Single Family Home', units:1, addr:'123 Maple St',    status:'Occupied', sc:'#16a34a', sb:'#dcfce7', icon:<FaHome />},
    {no:'No. 2', name:'Oak View Multi', type:'Multi Family Home',  units:3, addr:'456 Oak Ave',     status:'Partial',  sc:'#d97706', sb:'#fef3c7', icon:<FaBuilding />},
    {no:'No. 3', name:'Pine Ridge',     type:'Single Family Home', units:1, addr:'789 Pine Rd',     status:'Vacant',   sc:'#dc2626', sb:'#fee2e2', icon:<FaHome />},
    {no:'No. 4', name:'Elm Court',      type:'Multi Family Home',  units:2, addr:'321 Elm Blvd',   status:'Occupied', sc:'#16a34a', sb:'#dcfce7', icon:<FaBuilding />},
    {no:'No. 5', name:'Cedar Heights',  type:'Single Family Home', units:1, addr:'555 Cedar Lane',  status:'Occupied', sc:'#16a34a', sb:'#dcfce7', icon:<FaHome />},
  ];
  return (
    <div className="ui-app">
      <UiSidebar active="Properties" />
      <div className="ui-main">
        <div className="ui-topbar">
          <div>
            <p className="ui-page-title">Properties</p>
            <p className="ui-page-sub">Manage and view all your properties</p>
          </div>
          <div className="ui-search-bar"><span>🔍</span> Search properties...</div>
        </div>

        <div className="ui-chips-row">
          {chips.map(s => (
            <div className="ui-chip" key={s.l} style={{background:s.bg, border:`1px solid ${s.border||s.c+'44'}`}}>
              <small style={{color:s.c, fontWeight:700, fontSize:'9px', letterSpacing:'0.05em'}}>{s.l}</small>
              <strong style={{color:s.c, fontSize:'1.4rem'}}>{s.v}</strong>
            </div>
          ))}
        </div>

        <div className="ui-filter-row">
          <button className="ui-filter-btn ui-filter-active">+ Add Property</button>
          <button className="ui-filter-btn ui-filter-blue">Overview</button>
          {['SFH','MFH','Vacant','Partial','Occupied'].map(f => (
            <button key={f} className="ui-filter-btn">{f}</button>
          ))}
        </div>

        <div className="ui-prop-grid">
          {props.map(p => (
            <div key={p.name} className="ui-prop-card">
              <div className="ui-prop-card-top">
                <div className="ui-prop-icon-wrap" style={{background:p.sb, color:p.sc}}>{p.icon}</div>
                <div className="ui-prop-card-meta">
                  <span className="ui-prop-no">{p.no}</span>
                  <span className="ui-badge" style={{color:p.sc, background:p.sb}}>{p.status}</span>
                </div>
              </div>
              <div className="ui-prop-name">{p.name}</div>
              <div className="ui-prop-detail"><span>Property Type</span><strong>{p.type}</strong></div>
              <div className="ui-prop-detail"><span>No. of units</span><strong>{p.units}</strong></div>
              <div className="ui-prop-detail"><span>Address</span><strong>{p.addr}</strong></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MockFinancials() {
  const txns = [
    {no:1,  amt:'-$80',    type:'Expense',     prop:'Maple Grove',    vendor:'Window Co',   date:'2/10/2026', t:'ex'},
    {no:2,  amt:'-$100',   type:'Expense',     prop:'Oak View Multi', vendor:'Window Co',   date:'2/10/2026', t:'ex'},
    {no:3,  amt:'+$1,750', type:'Rent',        prop:'Maple Grove',    vendor:'Cash',        date:'2/1/2026',  t:'in'},
    {no:4,  amt:'+$1,500', type:'Rent',        prop:'Oak View Multi', vendor:'Zelle',       date:'2/1/2026',  t:'in'},
    {no:5,  amt:'+$2,000', type:'Rent',        prop:'Elm Court',      vendor:'Check',       date:'2/1/2026',  t:'in'},
    {no:6,  amt:'-$320',   type:'Repairs',     prop:'Pine Ridge',     vendor:'Plumber Co',  date:'1/28/2026', t:'ex'},
    {no:7,  amt:'-$145',   type:'Utilities',   prop:'Maple Grove',    vendor:'Water Dept',  date:'1/20/2026', t:'ex'},
    {no:8,  amt:'+$1,750', type:'Rent',        prop:'Maple Grove',    vendor:'Cash',        date:'1/1/2026',  t:'in'},
    {no:9,  amt:'-$200',   type:'Maintenance', prop:'Oak View Multi', vendor:'Handyman',    date:'1/15/2026', t:'ex'},
    {no:10, amt:'+$2,000', type:'Rent',        prop:'Cedar Heights',  vendor:'Venmo',       date:'1/1/2026',  t:'in'},
  ];
  return (
    <div className="ui-app">
      <UiSidebar active="Financials" />
      <div className="ui-main">
        <div className="ui-topbar">
          <p className="ui-page-title">Activity</p>
          <div className="ui-search-bar"><span>🔍</span> Search</div>
        </div>

        <div className="ui-filter-row">
          <button className="ui-filter-btn ui-filter-active">+ Add Activity</button>
          {['All','Income','Expenses','Maintenance','Repairs','Utilities','Other'].map((f,i) => (
            <button key={f} className={`ui-filter-btn ${i===0?'ui-filter-blue':''}`}>{f}</button>
          ))}
        </div>

        <div className="ui-act-table">
          <div className="ui-act-thead">
            <span>NO.</span><span>AMOUNT</span><span>TYPE</span>
            <span>PROPERTY NAME</span><span>VENDOR</span><span>DATE</span>
          </div>
          {txns.map(t => (
            <div key={t.no} className="ui-act-row">
              <span className="ui-act-no" style={{color:'#2563eb'}}>{t.no}.</span>
              <span className={t.t==='in'?'ui-amt-green':'ui-amt-red'} style={{fontWeight:700}}>{t.amt}</span>
              <span style={{color: t.t==='in'?'#16a34a':'#dc2626'}}>{t.type}</span>
              <span>{t.prop}</span>
              <span style={{color:'#6b7280'}}>{t.vendor}</span>
              <span style={{color:'#6b7280'}}>{t.date}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MockReports() {
  const props = [
    {no:'No.1', name:'Maple Grove',    type:'Single Family Home', units:1, addr:'123 Maple St',   status:'Occupied', sc:'#16a34a', sb:'#dcfce7'},
    {no:'No.2', name:'Oak View Multi', type:'Multi Family Home',  units:3, addr:'456 Oak Ave',    status:'Partial',  sc:'#d97706', sb:'#fef3c7'},
    {no:'No.3', name:'Pine Ridge',     type:'Single Family Home', units:1, addr:'789 Pine Rd',    status:'Vacant',   sc:'#dc2626', sb:'#fee2e2'},
    {no:'No.4', name:'Elm Court',      type:'Multi Family Home',  units:2, addr:'321 Elm Blvd',   status:'Occupied', sc:'#16a34a', sb:'#dcfce7'},
    {no:'No.5', name:'Cedar Heights',  type:'Single Family Home', units:1, addr:'555 Cedar Lane', status:'Occupied', sc:'#16a34a', sb:'#dcfce7'},
  ];
  return (
    <div className="ui-app">
      <UiSidebar active="Reports" />
      <div className="ui-main">
        <div className="ui-topbar">
          <p className="ui-page-title">Reports</p>
          <div className="ui-search-bar"><span>🔍</span> Search</div>
        </div>

        <div className="ui-info-banner">
          <strong>ⓘ  Select a property to show a Year-To-Date Report.</strong>
          <span>Reports show data at a property level. All units under a multifamily home are included under a single report.</span>
        </div>

        <div className="ui-prop-grid">
          {props.map(p => (
            <div key={p.name} className="ui-prop-card">
              <div className="ui-prop-card-top">
                <span className="ui-prop-no">{p.no}</span>
                <span className="ui-badge" style={{color:p.sc, background:p.sb}}>{p.status}</span>
              </div>
              <div className="ui-prop-name" style={{textAlign:'center', marginBottom:8}}>{p.name}</div>
              <div className="ui-prop-detail"><span>Property Type</span><strong>{p.type}</strong></div>
              <div className="ui-prop-detail"><span>No. of units</span><strong>{p.units}</strong></div>
              <div className="ui-prop-detail"><span>Address</span><strong>{p.addr}</strong></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MockCompare() {
  const info = [
    {label:'PROPERTY TYPE', a:'Multi Family Home',  b:'Single Family Home'},
    {label:'UNITS',         a:'3',                  b:'1'},
    {label:'STATUS',        a:'Partial',             b:'Occupied',
     aStyle:{color:'#d97706',bg:'#fef3c7'}, bStyle:{color:'#16a34a',bg:'#dcfce7'}},
    {label:'ADDRESS',       a:'456 Oak Ave',         b:'123 Maple St'},
  ];
  const financial = [
    {label:'PURCHASE PRICE', a:'$280,000', b:'$250,000'},
    {label:'TOTAL COST',     a:'$310,000', b:'$250,000'},
    {label:'MORTGAGE',       a:'$750',     b:'$500'},
    {label:'PROPERTY TAX',   a:'$2,200',   b:'$3,000'},
    {label:'INSURANCE',      a:'$500',     b:'$500'},
  ];
  const summary = [
    {label:'Income',        a:'$1,500', b:'$1,750', bg:'#dcfce7'},
    {label:'Expenses',      a:'$100',   b:'$80',    bg:'#fee2e2'},
    {label:'Net Cash Flow', a:'$1,400', b:'$1,670', bg:'#dbeafe'},
    {label:'ROI',           a:'1400%',  b:'1670%',  bg:'#ede9fe'},
  ];
  return (
    <div className="ui-app">
      <UiSidebar active="Compare" />
      <div className="ui-main" style={{gap:8, overflowY:'auto'}}>

        <div className="ui-topbar">
          <div>
            <p className="ui-page-title">Compare Properties</p>
            <p className="ui-page-sub">Select two properties to compare side by side</p>
          </div>
        </div>

        {/* selector row */}
        <div className="ui-cmp-selectors">
          <div className="ui-cmp-sel-wrap">
            <small>Property A</small>
            <div className="ui-cmp-select">Oak View Multi — 456 Oak Ave ▾</div>
          </div>
          <div className="ui-cmp-swap">⇄</div>
          <div className="ui-cmp-sel-wrap">
            <small>Property B</small>
            <div className="ui-cmp-select">Maple Grove — 123 Maple St ▾</div>
          </div>
        </div>

        {/* Property Info + Financial Details in one block */}
        <div className="ui-cmp-block">
          {/* prop name header */}
          <div className="ui-cmp-prop-headers">
            <div className="ui-cmp-ph"><FaBuilding style={{color:'#d97706'}} /> Oak View Multi</div>
            <div className="ui-cmp-ph"><FaHome style={{color:'#2563eb'}} /> Maple Grove</div>
          </div>

          <div className="ui-cmp-group-title">Property Info</div>
          {info.map(r => (
            <div key={r.label} className="ui-cmp-info-row">
              <div className="ui-cmp-info-a">{r.aStyle
                ? <span className="ui-badge" style={{color:r.aStyle.color,background:r.aStyle.bg}}>{r.a}</span>
                : <strong>{r.a}</strong>}
              </div>
              <div className="ui-cmp-info-label">{r.label}</div>
              <div className="ui-cmp-info-b">{r.bStyle
                ? <span className="ui-badge" style={{color:r.bStyle.color,background:r.bStyle.bg}}>{r.b}</span>
                : <strong>{r.b}</strong>}
              </div>
            </div>
          ))}

          <div className="ui-cmp-group-title">Financial Details</div>
          {financial.map(r => (
            <div key={r.label} className="ui-cmp-info-row">
              <div className="ui-cmp-info-a"><strong>{r.a}</strong></div>
              <div className="ui-cmp-info-label">{r.label}</div>
              <div className="ui-cmp-info-b"><strong>{r.b}</strong></div>
            </div>
          ))}
        </div>

        {/* Report Summary */}
        <div className="ui-cmp-group-title" style={{textAlign:'center'}}>Report Summary (Year-to-Date)</div>
        <div className="ui-cmp-summary-row">
          <div className="ui-cmp-summary-col">
            {summary.map(r => (
              <div key={r.label} className="ui-cmp-sum-card" style={{background:r.bg}}>
                <span>{r.label}</span><strong>{r.a}</strong>
              </div>
            ))}
          </div>
          <div className="ui-cmp-vs">VS</div>
          <div className="ui-cmp-summary-col">
            {summary.map(r => (
              <div key={r.label} className="ui-cmp-sum-card" style={{background:r.bg}}>
                <span>{r.label}</span><strong>{r.b}</strong>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

const ssTabs = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <FaHome />,
    color: '#2563eb',
    path: '/home',
    mock: <MockDashboard />,
    desc: 'Your full financial picture — income, expenses, occupancy, cash flow, and reminders, all on one screen.',
  },
  {
    id: 'properties',
    label: 'Properties',
    icon: <FaBuilding />,
    color: '#16a34a',
    path: '/properties',
    mock: <MockProperties />,
    desc: 'Browse, search, and filter every property and unit. See occupancy status at a glance and add new ones in seconds.',
  },
  {
    id: 'financials',
    label: 'Financials',
    icon: <FaDollarSign />,
    color: '#d97706',
    path: '/activity',
    mock: <MockFinancials />,
    desc: 'Log rent payments, one-time expenses, and recurring costs. Every transaction organized and searchable.',
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: <FaChartBar />,
    color: '#0891b2',
    path: '/reports',
    mock: <MockReports />,
    desc: 'Generate year-to-date reports for any property. See income, expenses, net cash flow, and ROI at a glance.',
  },
  {
    id: 'compare',
    label: 'Compare',
    icon: <FaExchangeAlt />,
    color: '#7c3aed',
    path: '/compare',
    mock: <MockCompare />,
    desc: 'Put any two properties side by side and instantly see which one is performing better.',
  },
];

const steps = [
  {
    num: '01',
    icon: <FaBuilding />,
    color: '#2563eb',
    bg: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
    title: 'Add Your Properties',
    desc: 'Set up your portfolio in minutes. Add units, set rent amounts, and assign occupancy status right away.',
  },
  {
    num: '02',
    icon: <FaClipboardList />,
    color: '#16a34a',
    bg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    title: 'Track Your Finances',
    desc: 'Log income and expenses, monitor cash flow month by month, and see your net profit in real time.',
  },
  {
    num: '03',
    icon: <FaLightbulb />,
    color: '#d97706',
    bg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
    title: 'Stay Ahead with Insights',
    desc: 'Get smart reminders for rent due dates, expiring leases, and insurance renewals before they sneak up on you.',
  },
];

function scrollToMailingList() {
  document.getElementById('mailing-list')?.scrollIntoView({ behavior: 'smooth' });
}

function LandingPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [activeMobTab, setActiveMobTab] = useState('mob-home');

  // Mailing list form state
  const [mlName, setMlName] = useState('');
  const [mlEmail, setMlEmail] = useState('');
  const [mlDoors, setMlDoors] = useState('');
  const [mlLoading, setMlLoading] = useState(false);
  const [mlSubmitted, setMlSubmitted] = useState(false);
  const [mlError, setMlError] = useState('');

  async function handleMailingList(e) {
    e.preventDefault();

    if (!mlName.trim() || !mlEmail.trim() || !mlDoors.trim()) {
        setMlError('Please fill in all fields.');
        return;
    }

    setMlLoading(true);
    setMlError('');

    try {
        const response = await fetch(
        `${BASE_URL}/AddToEmailList?full_name=${encodeURIComponent(mlName)}&email_address=${encodeURIComponent(mlEmail)}&number_of_doors=${encodeURIComponent(mlDoors)}`,
        {
            method: 'POST',
        }
        );

        if (!response.ok) {
        throw new Error('Failed request');
        }

        setMlSubmitted(true);
    } catch (err) {
        setMlError('Something went wrong. Please try again.');
    } finally {
        setMlLoading(false);
    }
  }

  return (
    <div className="lp-root">

      {/* ── NAV ── */}
      <nav className="lp-nav">
        <div className="lp-nav-brand">
          <h2 className="lp-nav-dtecx">DTecX</h2>
          <div className="lp-nav-product">
            <img src={logo} alt="GuideWell" className="lp-nav-logo" />
          </div>
        </div>
        <div className="lp-nav-actions">
          <button className="lp-btn-primary" onClick={scrollToMailingList}>
            Join the Mailing List
          </button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="lp-hero">
        <div className="lp-hero-bg-orb lp-orb-1" />
        <div className="lp-hero-bg-orb lp-orb-2" />
        <div className="lp-hero-bg-orb lp-orb-3" />

        <div className="lp-hero-inner">

          <div className="lp-hero-content">
            <div className="lp-hero-badge">
              <FaBolt className="lp-badge-icon" />
              Property Management Made Simple
            </div>

            <h1 className="lp-hero-title">
              Manage Your Properties
              <span className="lp-hero-title-highlight"> Smarter, Faster</span>
            </h1>

            <p className="lp-hero-subtitle">
              GuideWell is the all-in-one platform for property managers.
              Track portfolios, monitor financials, and stay ahead of every deadline —
              all from a single dashboard.
            </p>

            <div className="lp-hero-cta">
              <button className="lp-btn-hero-primary" onClick={scrollToMailingList}>
                Join the Mailing List
                <FaArrowRight className="lp-btn-arrow" />
              </button>
            </div>

            <div className="lp-hero-trust">
              {['Free to join', 'No spam, ever', 'Early access updates'].map((t) => (
                <span key={t} className="lp-trust-item">
                  <FaCheckCircle className="lp-trust-icon" />
                  {t}
                </span>
              ))}
            </div>
          </div>

          {/* mock dashboard visual */}
          <div className="lp-hero-visual">

            {/* top-right floating alert */}
            <div className="lp-mock-card lp-mock-card-float lp-float-a">
              <FaBell className="lp-float-icon lp-icon-orange" />
              <div>
                <p className="lp-float-title">Lease Expiring</p>
                <p className="lp-float-sub">Unit 4B — 3 days</p>
              </div>
            </div>

            {/* main dashboard card */}
            <div className="lp-mock-card lp-mock-card-main">
              <div className="lp-mock-header">
                <span className="lp-mock-dot lp-dot-green" />
                <span className="lp-mock-title">Dashboard</span>
              </div>

              <div className="lp-mock-stats-row">
                <div className="lp-mock-stat">
                  <span className="lp-mock-stat-num">24</span>
                  <span className="lp-mock-stat-lbl">Properties</span>
                </div>
                <div className="lp-mock-stat">
                  <span className="lp-mock-stat-num">91%</span>
                  <span className="lp-mock-stat-lbl">Occupancy</span>
                </div>
                <div className="lp-mock-stat">
                  <span className="lp-mock-stat-num lp-green">+12%</span>
                  <span className="lp-mock-stat-lbl">Net Profit</span>
                </div>
              </div>

              {/* cash flow bars */}
              <div className="lp-mock-bars">
                {[60, 80, 55, 90, 70, 85, 95].map((h, i) => (
                  <div key={i} className="lp-mock-bar-wrap">
                    <div className="lp-mock-bar" style={{ height: `${h}%` }} />
                  </div>
                ))}
              </div>

              {/* occupancy progress row */}
              <div className="lp-mock-divider" />
              <div className="lp-mock-occ-row">
                <span className="lp-mock-occ-lbl">Occupancy Rate</span>
                <div className="lp-mock-occ-track">
                  <div className="lp-mock-occ-fill" style={{ width: '91%' }} />
                </div>
                <span className="lp-mock-occ-pct">91%</span>
              </div>
            </div>

            {/* bottom two mini cards */}
            <div className="lp-mock-mini-row">
              <div className="lp-mock-card lp-mini-card">
                <div className="lp-mini-icon-wrap lp-mini-green">
                  <FaDollarSign />
                </div>
                <div>
                  <p className="lp-float-title">Rent Collected</p>
                  <p className="lp-float-sub">$18,450 this month</p>
                </div>
              </div>

              <div className="lp-mock-card lp-mini-card">
                <div className="lp-mini-icon-wrap lp-mini-blue">
                  <FaBuilding />
                </div>
                <div>
                  <p className="lp-float-title">Vacant Units</p>
                  <p className="lp-float-sub">3 of 24 available</p>
                </div>
              </div>
            </div>

            {/* monthly financial summary */}
            <div className="lp-mock-card lp-financial-summary">
              <div className="lp-fs-item">
                <span className="lp-fs-dot lp-fs-green-dot" />
                <span className="lp-fs-label">Total Income</span>
                <span className="lp-fs-value lp-fs-green">$18,450</span>
              </div>
              <div className="lp-fs-sep" />
              <div className="lp-fs-item">
                <span className="lp-fs-dot lp-fs-red-dot" />
                <span className="lp-fs-label">Expenses</span>
                <span className="lp-fs-value lp-fs-red">$5,230</span>
              </div>
              <div className="lp-fs-sep" />
              <div className="lp-fs-item">
                <span className="lp-fs-dot lp-fs-blue-dot" />
                <span className="lp-fs-label">Net Profit</span>
                <span className="lp-fs-value lp-fs-blue">$13,220</span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="lp-how">
        <div className="lp-section-header">
          <h2 className="lp-section-title">Up and running in three steps</h2>
          <p className="lp-section-sub">
            No complicated setup. Just add your properties and GuideWell handles the rest.
          </p>
        </div>

        <div className="lp-steps-row">
          {steps.map((s, i) => (
            <div key={s.num} className="lp-step-wrap">
              <div className="lp-step-card" style={{ background: s.bg }}>
                <div className="lp-step-top">
                  <span className="lp-step-num" style={{ color: s.color }}>{s.num}</span>
                  <div className="lp-step-icon" style={{ color: s.color, background: '#fff' }}>
                    {s.icon}
                  </div>
                </div>
                <h3 className="lp-step-title">{s.title}</h3>
                <p className="lp-step-desc">{s.desc}</p>
              </div>

              {i < steps.length - 1 && (
                <div className="lp-step-arrow">
                  <FaArrowRight />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── SCREENSHOTS ── */}
      <section className="lp-screenshots">
        <div className="lp-section-header">
          <h2 className="lp-section-title">See GuideWell in action</h2>
          <p className="lp-section-sub">
            A clean, intuitive interface built for property managers who mean business.
          </p>
        </div>

        {/* Desktop tab showcase */}
        <div className="lp-ss-wrapper">

          <div className="lp-ss-tab-row">
            {ssTabs.map((t) => (
              <button
                key={t.id}
                className={`lp-ss-tab ${activeTab === t.id ? 'lp-ss-tab-active' : ''}`}
                onClick={() => setActiveTab(t.id)}
                style={activeTab === t.id ? { borderColor: t.color, color: t.color, background: '#fff' } : {}}
              >
                <span className="lp-ss-tab-icon">{t.icon}</span>
                {t.label}
              </button>
            ))}
          </div>

          <div className="lp-ss-browser">
            <div className="lp-ss-browser-chrome">
              <div className="lp-ss-chrome-dots">
                <span className="lp-chrome-dot lp-dot-red" />
                <span className="lp-chrome-dot lp-dot-yellow" />
                <span className="lp-chrome-dot lp-dot-green2" />
              </div>
              <div className="lp-ss-chrome-bar">
                guidewell.app{ssTabs.find((t) => t.id === activeTab)?.path}
              </div>
            </div>

            <div className="lp-ss-screen-area">
              {ssTabs.map((t) => (
                <div
                  key={t.id}
                  className={`lp-ss-frame ${activeTab === t.id ? 'lp-ss-frame-visible' : ''}`}
                >
                  {t.mock}
                </div>
              ))}
            </div>
          </div>

          <p className="lp-ss-tab-desc">
            {ssTabs.find((t) => t.id === activeTab)?.desc}
          </p>
        </div>

        {/* ── MOBILE SECTION ── */}
        <div className="lp-mob-section">
          <div className="lp-section-header">
            <h2 className="lp-section-title">Works beautifully on mobile too</h2>
            <p className="lp-section-sub">
              Manage your portfolio from anywhere with the GuideWell mobile app.
            </p>
          </div>

          {/* Tab selector */}
          <div className="lp-mob-tab-row">
            {[
              { id: 'mob-home',       label: 'Home',       icon: <FaHome /> },
              { id: 'mob-activity',   label: 'Activity',   icon: <FaClipboardList /> },
              { id: 'mob-properties', label: 'Properties', icon: <FaBuilding /> },
            ].map(t => (
              <button
                key={t.id}
                className={`lp-mob-tab ${activeMobTab === t.id ? 'lp-mob-tab-active' : ''}`}
                onClick={() => setActiveMobTab(t.id)}
              >
                <span>{t.icon}</span>{t.label}
              </button>
            ))}
          </div>

          {/* Phone shell */}
          <div className="lp-mob-phone-wrap">
            <div className="lp-mob-phone">
              <div className="lp-mob-notch" />
              <div className="lp-mob-screen">

                {/* HOME */}
                {activeMobTab === 'mob-home' && (
                  <div className="mob-app">
                    <div className="mob-topbar">
                      <span className="mob-title">Home</span>
                      <div className="mob-topbar-icons">
                        <span className="mob-icon-btn">👤</span>
                        <span className="mob-icon-btn mob-icon-plus">+</span>
                      </div>
                    </div>
                    <div className="mob-home-scroll">
                      <div className="mob-lease-card">
                        <div className="mob-lease-title">Lease Ending</div>
                        <div className="mob-lease-prop">Multi Test #102</div>
                        <div className="mob-lease-date">Ends Jan 8, 2026</div>
                        <div className="mob-lease-count">1 of 3</div>
                      </div>
                      <div className="mob-stat-grid">
                        <div className="mob-stat-card">
                          <span className="mob-stat-label">April Income</span>
                          <span className="mob-stat-val mob-green">$1,000.00</span>
                          <span className="mob-stat-tag mob-green">↗ 0.0%</span>
                        </div>
                        <div className="mob-stat-card">
                          <span className="mob-stat-label">April Expenses</span>
                          <span className="mob-stat-val mob-red">$500.00</span>
                          <span className="mob-stat-tag mob-red">↗ 0.0%</span>
                        </div>
                        <div className="mob-stat-card">
                          <span className="mob-stat-label">Occupancy</span>
                          <span className="mob-stat-val mob-orange">42%</span>
                          <div className="mob-occ-bar"><div className="mob-occ-fill" style={{width:'42%'}} /></div>
                        </div>
                        <div className="mob-stat-card">
                          <span className="mob-stat-label">Total Profit</span>
                          <span className="mob-stat-val mob-green">$500.00</span>
                        </div>
                      </div>
                      <div className="mob-chart-card">
                        <div className="mob-chart-header">
                          <span className="mob-chart-title">Cash Flow</span>
                          <span className="mob-chart-ytd">YTD ▾</span>
                        </div>
                        <div className="mob-line-chart">
                          <svg viewBox="0 0 200 60" preserveAspectRatio="none" style={{width:'100%',height:'60px'}}>
                            <defs>
                              <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3"/>
                                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0"/>
                              </linearGradient>
                            </defs>
                            <path d="M0,58 C20,58 30,50 50,35 C70,20 90,10 110,8 C130,6 150,8 200,8" fill="none" stroke="#3b82f6" strokeWidth="2.5"/>
                            <path d="M0,58 C20,58 30,50 50,35 C70,20 90,10 110,8 C130,6 150,8 200,8 L200,60 L0,60Z" fill="url(#cg)"/>
                          </svg>
                          <div className="mob-chart-labels">
                            <span>Jan '26</span><span>Feb '26</span><span>Apr '26</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mob-bottom-nav">
                      <span className="mob-nav-item mob-nav-active"><FaHome /><small>Home</small></span>
                      <span className="mob-nav-item"><FaBuilding /><small>Properties</small></span>
                      <span className="mob-nav-item"><FaClipboardList /><small>Activity</small></span>
                      <span className="mob-nav-item"><FaBolt /><small>Coming Soon</small></span>
                    </div>
                  </div>
                )}

                {/* ACTIVITY */}
                {activeMobTab === 'mob-activity' && (
                  <div className="mob-app">
                    <div className="mob-topbar">
                      <span className="mob-title">Activity</span>
                      <div className="mob-topbar-icons">
                        <span className="mob-icon-btn">☰</span>
                        <span className="mob-icon-btn">👤</span>
                        <span className="mob-icon-btn mob-icon-plus">+</span>
                      </div>
                    </div>
                    <div className="mob-txn-list">
                      {[
                        {icon:'🔧', iconBg:'#2563eb', title:'Heater Work Order', prop:'Ai Test #3',        date:'Apr 8 at 8:42 PM',  amt:'-$500.00',  t:'ex'},
                        {icon:'💵', iconBg:'#16a34a', title:'Rent Payment',      prop:'Multi Test - 102',  date:'Apr 6 at 6:52 PM',  amt:'+$1,000.00', t:'in'},
                        {icon:'🔧', iconBg:'#2563eb', title:'Repairs',           prop:'Friday Test re',    date:'Feb 14 at 2:02 PM', amt:'-$500.00',  t:'ex'},
                        {icon:'💵', iconBg:'#16a34a', title:'Rent Payment',      prop:'Tsestasd',          date:'Feb 12 at 11:22 PM',amt:'+$4.00',    t:'in'},
                        {icon:'🏠', iconBg:'#d97706', title:'HOA',               prop:'Friday Test re',    date:'Feb 8 at 11:46 AM', amt:'-$50.00',   t:'ex'},
                        {icon:'💵', iconBg:'#16a34a', title:'Rent Payment',      prop:'Friday Test re',    date:'Feb 7 at 12:08 PM', amt:'+$25,000',  t:'in'},
                      ].map((txn, i) => (
                        <div key={i} className="mob-txn-row">
                          <div className="mob-txn-icon" style={{background:txn.iconBg}}>{txn.icon}</div>
                          <div className="mob-txn-info">
                            <span className="mob-txn-title">{txn.title}</span>
                            <span className="mob-txn-prop">{txn.prop}</span>
                          </div>
                          <div className="mob-txn-right">
                            <span className={txn.t==='in'?'mob-green':'mob-red'} style={{fontWeight:700,fontSize:'13px'}}>{txn.amt}</span>
                            <span className="mob-txn-date">{txn.date}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mob-bottom-nav">
                      <span className="mob-nav-item"><FaHome /><small>Home</small></span>
                      <span className="mob-nav-item"><FaBuilding /><small>Properties</small></span>
                      <span className="mob-nav-item mob-nav-active"><FaClipboardList /><small>Activity</small></span>
                      <span className="mob-nav-item"><FaBolt /><small>Coming Soon</small></span>
                    </div>
                  </div>
                )}

                {/* PROPERTIES */}
                {activeMobTab === 'mob-properties' && (
                  <div className="mob-app">
                    <div className="mob-topbar">
                      <span className="mob-title">Properties</span>
                      <div className="mob-topbar-icons">
                        <span className="mob-icon-btn">👤</span>
                        <span className="mob-icon-btn mob-icon-plus">+</span>
                      </div>
                    </div>
                    <div className="mob-prop-chips">
                      {[{v:'35',l:'Total Units',c:'#3b82f6'},{v:'14',l:'Occupied',c:'#16a34a',active:true},{v:'21',l:'Vacant',c:'#d97706'}].map(c=>(
                        <div key={c.l} className={`mob-prop-chip ${c.active?'mob-chip-active':''}`} style={c.active?{borderColor:'#16a34a'}:{}}>
                          <strong style={{color:c.c,fontSize:'1.4rem'}}>{c.v}</strong>
                          <small style={{color:'#9ca3af'}}>{c.l}</small>
                        </div>
                      ))}
                    </div>
                    <div className="mob-filter-tabs">
                      {['All','Single Family','Multi Family'].map((f,i)=>(
                        <button key={f} className={`mob-filter-tab ${i===0?'mob-filter-active':''}`}>{f}</button>
                      ))}
                    </div>
                    <div className="mob-prop-list">
                      {[
                        {name:'Friday Test re', addr:'1209 Reoad st', status:'OCCUPIED · 1 UNIT', sc:'#16a34a', sb:'rgba(22,163,74,0.12)'},
                        {name:'Maple Grove',    addr:'123 Maple St',  status:'OCCUPIED · 1 UNIT', sc:'#16a34a', sb:'rgba(22,163,74,0.12)'},
                        {name:'Pine Ridge',     addr:'789 Pine Rd',   status:'VACANT · 1 UNIT',   sc:'#dc2626', sb:'rgba(220,38,38,0.12)'},
                      ].map(p=>(
                        <div key={p.name} className="mob-prop-card" style={{borderLeft:`3px solid ${p.sc}`}}>
                          <div className="mob-prop-card-header">
                            <span className="mob-prop-badge" style={{color:p.sc, background:p.sb}}>{p.status}</span>
                          </div>
                          <div className="mob-prop-info">
                            <strong>{p.name}</strong>
                            <small>📍 {p.addr}</small>
                          </div>
                          <button className="mob-view-btn"><FaChartBar /> View Details <span className="mob-view-chevron">▾</span></button>
                        </div>
                      ))}
                    </div>
                    <div className="mob-bottom-nav">
                      <span className="mob-nav-item"><FaHome /><small>Home</small></span>
                      <span className="mob-nav-item mob-nav-active"><FaBuilding /><small>Properties</small></span>
                      <span className="mob-nav-item"><FaClipboardList /><small>Activity</small></span>
                      <span className="mob-nav-item"><FaBolt /><small>Coming Soon</small></span>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>

      </section>

      {/* ── FEATURES ── */}
      <section className="lp-features">
        <div className="lp-section-header">
          <h2 className="lp-section-title">Everything you need to manage your portfolio</h2>
          <p className="lp-section-sub">
            Powerful tools built for modern property managers, all in one place.
          </p>
        </div>

        <div className="lp-features-grid">
          {features.map((f) => (
            <div className="lp-feature-card" key={f.title} style={{ background: f.bg }}>
              <div className="lp-feature-icon" style={{ color: f.color }}>
                {f.icon}
              </div>
              <h3 className="lp-feature-title" style={{ color: '#1a1a2e' }}>{f.title}</h3>
              <p className="lp-feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── MAILING LIST ── */}
      <section className="lp-ml-section" id="mailing-list">
        <div className="lp-cta-orb lp-cta-orb-1" />
        <div className="lp-cta-orb lp-cta-orb-2" />
        <div className="lp-ml-inner">

          <div className="lp-ml-badge">
            <FaEnvelope className="lp-badge-icon" />
            Stay in the Loop
          </div>

          <h2 className="lp-ml-title">Join the GuideWell Mailing List</h2>
          <p className="lp-ml-sub">
            Be the first to know when we launch. Get early access, feature updates,
            and exclusive tips straight to your inbox.
          </p>

          {mlSubmitted ? (
            <div className="lp-ml-success">
              <FaCheckCircle className="lp-ml-success-icon" />
              <p className="lp-ml-success-title">You're on the list!</p>
              <p className="lp-ml-success-sub">We'll reach out as soon as GuideWell is ready for you.</p>
            </div>
          ) : (
            <form className="lp-ml-form" onSubmit={handleMailingList}>
              <div className="lp-ml-fields">
                <input
                  className="lp-ml-input"
                  type="text"
                  placeholder="Full Name"
                  value={mlName}
                  onChange={(e) => setMlName(e.target.value)}
                  disabled={mlLoading}
                />
                <input
                  className="lp-ml-input"
                  type="email"
                  placeholder="Email Address"
                  value={mlEmail}
                  onChange={(e) => setMlEmail(e.target.value)}
                  disabled={mlLoading}
                />
              </div>
              <div className="lp-ml-fields-row">
                <input
                  className="lp-ml-input lp-ml-input-doors"
                  type="number"
                  min="0"
                  placeholder="Number of Doors"
                  value={mlDoors}
                  onChange={(e) => setMlDoors(e.target.value)}
                  disabled={mlLoading}
                />
              </div>

              {mlError && <p className="lp-ml-error">{mlError}</p>}

              <button
                className="lp-btn-cta-primary"
                type="submit"
                disabled={mlLoading}
              >
                {mlLoading ? 'Joining...' : 'Join the List'}
                {!mlLoading && <FaArrowRight className="lp-btn-arrow" />}
              </button>

              <p className="lp-ml-disclaimer">No spam, ever. Unsubscribe anytime.</p>
            </form>
          )}

        </div>
      </section>

    </div>
  );
}

export default LandingPage;
